import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {AddShaderSockets} from "#material/nodes/shader/add-shader/definition"
import {addShaderDefinition} from "#material/nodes/shader/add-shader/definition"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {ThreeReturns} from "#material/sockets/three"
import type {AddClosureNode} from "#material/cycles/types"
import {asFloat, asVec3, threeRGBColor, threeValue} from "#material/three/three-utils"
import {Three as THREE, ThreeTsl as TSL} from "#material/three/exports"

@registerMaterialGraphNode
export class AddShader extends MaterialNode<AddShaderSockets> {
    public override get definition() {
        return addShaderDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<AddShaderSockets>> => {
        const material = new ThreeColormassMaterial()
        const {logger} = evaluator.context

        const {shader, shader_001} = await evaluator.getAll({
            shader: this.parameters.inputs.shader,
            shader_001: this.parameters.inputs.shader_001,
        })

        if (!(shader instanceof THREE.MeshPhysicalNodeMaterial) || !(shader_001 instanceof THREE.MeshPhysicalNodeMaterial)) {
            if (shader) {
                if (!(shader instanceof THREE.Material)) throw new Error("AddShader: shader input must be a THREE.Material in ThreeJS mode")
                return {shader: shader}
            } else if (shader_001) {
                if (!(shader_001 instanceof THREE.Material)) throw new Error("AddShader: shader_001 input must be a THREE.Material in ThreeJS mode")
                return {shader: shader_001}
            } else return {shader: material}
        }

        const addVec3 = (a: THREE.Node, b: THREE.Node) => TSL.add(asVec3(a), asVec3(b))
        const addFloat = (a: THREE.Node, b: THREE.Node) => TSL.add(asFloat(a), asFloat(b))

        const combine = <T, R>(a: T | null | undefined, b: T | null | undefined, fallback: T | null | undefined, op: (a: T, b: T) => R): R | T | null => {
            if (a == null && b == null) return null
            if (fallback == null) {
                if (a == null) return b ?? null
                if (b == null) return a
                return op(a, b)
            }
            return op(a ?? fallback, b ?? fallback)
        }

        material.colorNode = combine(shader.colorNode, shader_001.colorNode, threeRGBColor({r: 1, g: 1, b: 1}), addVec3) ?? material.colorNode
        material.roughnessNode = combine(shader.roughnessNode, shader_001.roughnessNode, threeValue(1), addFloat) ?? material.roughnessNode
        material.metalnessNode = combine(shader.metalnessNode, shader_001.metalnessNode, threeValue(0), addFloat) ?? material.metalnessNode
        material.iorNode = combine(shader.iorNode, shader_001.iorNode, threeValue(1.5), addFloat) ?? material.iorNode
        material.specularColorNode =
            combine(shader.specularColorNode, shader_001.specularColorNode, threeRGBColor({r: 1, g: 1, b: 1}), addVec3) ?? material.specularColorNode
        material.sheenNode = combine(shader.sheenNode, shader_001.sheenNode, threeValue(0), addFloat) ?? material.sheenNode
        material.clearcoatNode = combine(shader.clearcoatNode, shader_001.clearcoatNode, threeValue(0), addFloat) ?? material.clearcoatNode
        material.clearcoatRoughnessNode =
            combine(shader.clearcoatRoughnessNode, shader_001.clearcoatRoughnessNode, threeValue(0), addFloat) ?? material.clearcoatRoughnessNode
        material.clearcoatNormalNode = combine(shader.clearcoatNormalNode, shader_001.clearcoatNormalNode, null, addVec3) ?? material.clearcoatNormalNode
        material.normalNode = combine(shader.normalNode, shader_001.normalNode, null, addVec3) ?? material.normalNode
        material.emissiveNode = combine(shader.emissiveNode, shader_001.emissiveNode, threeRGBColor({r: 0, g: 0, b: 0}), addVec3) ?? material.emissiveNode
        const alphaValue = combine(shader.opacityNode, shader_001.opacityNode, threeValue(1), addFloat) ?? material.opacityNode
        const transmissionValue = combine(shader.transmissionNode, shader_001.transmissionNode, threeValue(0), addFloat) ?? material.transmissionNode

        if (alphaValue) {
            if (transmissionValue) logger.warn("Material uses both alpha and transmission! Preferring alpha.")
            material.transparent = shader.transparent || shader_001.transparent
            material.opacityNode = alphaValue
        } else if (transmissionValue) {
            material.transparent = shader.transparent || shader_001.transparent
            material.transmissionNode = transmissionValue
            material.transmission = 0.01
        }

        return {shader: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<AddShaderSockets>> => {
        const addClosureNode: AddClosureNode = {
            type: "add_closure",
            inputs: await evaluator.getAll({
                closure1: this.parameters.inputs.shader,
                closure2: this.parameters.inputs.shader_001,
            }),
            parameters: {},
            resources: {},
        }

        return {shader: [addClosureNode, "closure"]}
    }
}
